import { useState } from "react";
import { CheckCircle2, Loader2, Mail, ShieldCheck } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { trackNewsletterSubscription } from "@/lib/analytics";

type FormState = "idle" | "submitting" | "success" | "error";

interface NewsletterSubscriptionFormProps {
  sourceSlug: string;
}

/**
 * A marketing-consent capture for timely AI news. It is intentionally separate
 * from transactional guide/audit requests: consent is explicit, recorded with
 * its version and source, and no email address is sent to analytics.
 */
export default function NewsletterSubscriptionForm({ sourceSlug }: NewsletterSubscriptionFormProps) {
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [hasTrackedStart, setHasTrackedStart] = useState(false);

  const subscribe = trpc.newsletter.subscribe.useMutation({
    onSuccess: () => {
      setFormState("success");
      trackNewsletterSubscription({ post_slug: sourceSlug, action: "form_success" });
    },
    onError: (error) => {
      setErrorMessage(error.message || "We could not save your subscription. Please try again.");
      setFormState("error");
      trackNewsletterSubscription({ post_slug: sourceSlug, action: "form_error" });
    },
  });

  const handleEmailFocus = () => {
    if (hasTrackedStart) return;
    setHasTrackedStart(true);
    trackNewsletterSubscription({ post_slug: sourceSlug, action: "form_start" });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim() || !marketingConsent) return;

    setFormState("submitting");
    setErrorMessage("");
    trackNewsletterSubscription({ post_slug: sourceSlug, action: "form_submit" });
    subscribe.mutate({
      email: email.trim(),
      sourceSlug,
      marketingConsent: true,
      companyWebsite: companyWebsite.trim() || undefined,
    });
  };

  return (
    <section
      id="future-ai-updates"
      aria-labelledby="future-ai-updates-title"
      className="my-12 overflow-hidden rounded-2xl border border-teal-300/25 bg-gradient-to-br from-teal-400/10 via-slate-900 to-amber-400/10"
    >
      <div className="h-1 bg-gradient-to-r from-teal-300 via-cyan-300 to-amber-300" />
      <div className="p-6 sm:p-8">
        {formState === "success" ? (
          <div className="mx-auto max-w-2xl text-center" role="status">
            <CheckCircle2 className="mx-auto h-10 w-10 text-teal-300" aria-hidden="true" />
            <h2 id="future-ai-updates-title" className="mt-3 font-serif text-2xl font-semibold text-white">
              You&apos;re subscribed to future AI updates.
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              We&apos;ll email you when a frontier-model, AI safety or AI marketing development is ready for an evidence-led briefing. You can withdraw consent at any time.
            </p>
          </div>
        ) : (
          <div className="grid gap-7 lg:grid-cols-[1.35fr_0.85fr] lg:items-center">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-teal-200">
                <Mail className="h-4 w-4" aria-hidden="true" />
                Future AI updates
              </div>
              <h2 id="future-ai-updates-title" className="mt-3 font-serif text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                Get the next frontier AI briefing in your inbox
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300">
                Receive concise, source-qualified analysis when a model launch, safety finding or policy change has practical implications for AI marketing, search or agentic workflows.
              </p>
              <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-slate-400">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" aria-hidden="true" />
                This is an optional marketing subscription. It is not required to read the article or request any free resource.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="rounded-xl border border-white/10 bg-slate-950/50 p-5 shadow-xl" noValidate>
              <label htmlFor="future-ai-updates-email" className="text-sm font-medium text-slate-100">
                Email address
              </label>
              <input
                id="future-ai-updates-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onFocus={handleEmailFocus}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                className="mt-2 min-w-0 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-300/30"
                aria-describedby={formState === "error" ? "future-ai-updates-error" : "future-ai-updates-consent-note"}
              />

              <div className="mt-4 flex items-start gap-3">
                <input
                  id="future-ai-updates-consent"
                  name="marketing-consent"
                  type="checkbox"
                  required
                  checked={marketingConsent}
                  onChange={(event) => {
                    setMarketingConsent(event.target.checked);
                    if (event.target.checked) {
                      trackNewsletterSubscription({ post_slug: sourceSlug, action: "consent_checked" });
                    }
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-500 bg-slate-900 text-teal-400 focus:ring-2 focus:ring-teal-300/40"
                />
                <label htmlFor="future-ai-updates-consent" className="text-xs leading-relaxed text-slate-300">
                  I agree that Integrated.Social may contact me by email with future AI news, analysis, services and marketing updates. I can withdraw consent at any time. Read the{" "}
                  <a href="/privacy-policy/" className="text-teal-200 underline underline-offset-2 hover:text-teal-100">Privacy Policy</a>.
                </label>
              </div>

              <div className="sr-only" aria-hidden="true">
                <label htmlFor="future-ai-updates-company-website">Company website</label>
                <input
                  id="future-ai-updates-company-website"
                  name="companyWebsite"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={companyWebsite}
                  onChange={(event) => setCompanyWebsite(event.target.value)}
                />
              </div>

              {formState === "error" ? (
                <p id="future-ai-updates-error" className="mt-3 text-sm text-rose-300" role="alert">{errorMessage}</p>
              ) : null}

              <button
                type="submit"
                disabled={formState === "submitting" || !email.trim() || !marketingConsent}
                className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-300 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-teal-200 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.97]"
              >
                {formState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Mail className="h-4 w-4" aria-hidden="true" />}
                {formState === "submitting" ? "Saving your subscription…" : "Subscribe to AI updates"}
              </button>
              <p id="future-ai-updates-consent-note" className="mt-3 text-xs leading-relaxed text-slate-400">
                Consent is required before subscribing. We record the consent version and source article, not your reading activity or email address in analytics.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
